/**
 * Network Configuration - Central source of truth for mainnet/testnet settings
 *
 * All API services read from this module. When the wallet connects on a testnet,
 * call setTestnet(true) to switch all addresses, endpoints, and chain IDs globally.
 */

export type NetworkMode = 'mainnet' | 'testnet';

// ---------------------------------------------------------------------------
// Global network state
// ---------------------------------------------------------------------------
let _isTestnet = false;
const _listeners: Array<(isTestnet: boolean) => void> = [];

export function isTestnet(): boolean {
  return _isTestnet;
}

export function setTestnet(value: boolean): void {
  if (_isTestnet !== value) {
    _isTestnet = value;
    _listeners.forEach(fn => fn(value));
  }
}

export function onNetworkChange(listener: (isTestnet: boolean) => void): () => void {
  _listeners.push(listener);
  return () => {
    const idx = _listeners.indexOf(listener);
    if (idx >= 0) _listeners.splice(idx, 1);
  };
}

export function getNetworkMode(): NetworkMode {
  return _isTestnet ? 'testnet' : 'mainnet';
}

// ---------------------------------------------------------------------------
// Chain IDs – used by wallets to identify networks
// ---------------------------------------------------------------------------

// Known testnet chain IDs for auto-detection from wallet
export const TESTNET_EVM_CHAIN_IDS = new Set([
  11155111,  // Ethereum Sepolia
  84532,     // Base Sepolia
  421614,    // Arbitrum Sepolia
  11155420,  // Optimism Sepolia
  80002,     // Polygon Amoy
  43113,     // Avalanche Fuji
  97,        // BSC Testnet
]);

export const MAINNET_EVM_CHAIN_IDS = new Set([
  1,       // Ethereum
  8453,    // Base
  42161,   // Arbitrum
  10,      // Optimism
  137,     // Polygon
  43114,   // Avalanche
  56,      // BSC
]);

/**
 * Detect if a wallet is on testnet based on chain IDs it reports.
 * Handles decimal strings, hex strings (0x...), and numbers.
 */
export function detectTestnetFromChainId(chainId: number | string): boolean {
  let id: number;
  if (typeof chainId === 'string') {
    id = chainId.startsWith('0x') ? parseInt(chainId, 16) : parseInt(chainId, 10);
  } else {
    id = chainId;
  }
  return TESTNET_EVM_CHAIN_IDS.has(id);
}

// ---------------------------------------------------------------------------
// EVM Chain IDs per network
// ---------------------------------------------------------------------------
const EVM_CHAIN_IDS = {
  mainnet: {
    ethereum: '1',
    base: '8453',
    arbitrum: '42161',
    optimism: '10',
    polygon: '137',
    avalanche: '43114',
    bsc: '56',
  },
  testnet: {
    ethereum: '11155111',   // Sepolia
    base: '84532',          // Base Sepolia
    arbitrum: '421614',     // Arbitrum Sepolia
    optimism: '11155420',   // Optimism Sepolia
    polygon: '80002',       // Polygon Amoy
    avalanche: '43113',     // Avalanche Fuji
    bsc: '97',              // BSC Testnet
  },
} as const;

export function getEvmChainId(chain: string): string {
  const mode = _isTestnet ? 'testnet' : 'mainnet';
  const key = chain.toLowerCase().replace(' ', '') as keyof typeof EVM_CHAIN_IDS.mainnet;
  // Handle aliases
  if (key === 'bnbchain' as any) return EVM_CHAIN_IDS[mode].bsc;
  return (EVM_CHAIN_IDS[mode] as any)[key] || '';
}

// ---------------------------------------------------------------------------
// Skip Go Configuration
// ---------------------------------------------------------------------------
const SKIP_GO_CONFIG = {
  mainnet: {
    apiBase: 'https://api.skip.build',
    chainIdMap: {
      'Ethereum': '1',
      'Base': '8453',
      'Arbitrum': '42161',
      'Optimism': '10',
      'Polygon': '137',
      'Avalanche': '43114',
      'BNB Chain': '56',
      'Osmosis': 'osmosis-1',
      'Cosmos Hub': 'cosmoshub-4',
      'Noble': 'noble-1',
      'Celestia': 'celestia',
      'Solana': 'solana',
    } as Record<string, string>,
    tokenDenomMap: {
      'USDC': {
        '1': '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
        '8453': '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
        '42161': '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
        '10': '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
        '137': '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
        'noble-1': 'uusdc',
        'solana': 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
      } as Record<string, string>,
      'USDT': {
        '1': '0xdAC17F958D2ee523a2206206994597C13D831ec7',
        '8453': '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
        '42161': '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
        '10': '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58',
        '137': '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      } as Record<string, string>,
      'ETH': {
        '1': 'ethereum-native',
        '8453': 'base-native',
        '42161': 'arbitrum-native',
        '10': 'optimism-native',
      } as Record<string, string>,
    } as Record<string, Record<string, string>>,
    nativeDenomMap: {
      '1': 'ethereum-native',
      '8453': 'base-native',
      '42161': 'arbitrum-native',
      '10': 'optimism-native',
      '137': 'polygon-native',
      '43114': 'avalanche-native',
      '56': 'bsc-native',
      'osmosis-1': 'uosmo',
      'cosmoshub-4': 'uatom',
      'noble-1': 'uusdc',
      'celestia': 'utia',
      'solana': 'solana-native',
    } as Record<string, string>,
  },
  testnet: {
    apiBase: 'https://api.skip.build', // Skip Go supports testnets on same API
    chainIdMap: {
      'Ethereum': '11155111',       // Sepolia
      'Base': '84532',              // Base Sepolia
      'Arbitrum': '421614',         // Arbitrum Sepolia
      'Optimism': '11155420',       // Optimism Sepolia
      'Polygon': '80002',           // Amoy
      'Avalanche': '43113',         // Fuji
      'BNB Chain': '97',            // BSC Testnet
      'Osmosis': 'osmo-test-5',
      'Cosmos Hub': 'theta-testnet-001',
      'Noble': 'grand-1',
      'Celestia': 'mocha-4',
      'Solana': 'solana-devnet',
    } as Record<string, string>,
    tokenDenomMap: {
      'USDC': {
        '11155111': '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238',  // Sepolia USDC
        '84532': '0x036CbD53842c5426634e7929541eC2318f3dCF7e',     // Base Sepolia USDC
        '421614': '0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d',    // Arb Sepolia USDC
        '11155420': '0x5fd84259d66Cd46123540766Be93DFE6D43130D7',  // OP Sepolia USDC
        '80002': '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',    // Amoy USDC
        'grand-1': 'uusdc',
        'solana-devnet': '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU', // Devnet USDC
      } as Record<string, string>,
      'USDT': {
        '11155111': '0x7169D38820dfd117C3FA1f22a697dBA58d90BA06',  // Sepolia USDT
        '421614': '0x30aB3E244E25A9e43e4396A91F4F162aBb3aD7b3',   // Arb Sepolia USDT
      } as Record<string, string>,
      'ETH': {
        '11155111': 'ethereum-native',
        '84532': 'base-native',
        '421614': 'arbitrum-native',
        '11155420': 'optimism-native',
      } as Record<string, string>,
    } as Record<string, Record<string, string>>,
    nativeDenomMap: {
      '11155111': 'ethereum-native',
      '84532': 'base-native',
      '421614': 'arbitrum-native',
      '11155420': 'optimism-native',
      '80002': 'polygon-native',
      '43113': 'avalanche-native',
      '97': 'bsc-native',
      'osmo-test-5': 'uosmo',
      'theta-testnet-001': 'uatom',
      'grand-1': 'uusdc',
      'mocha-4': 'utia',
      'solana-devnet': 'solana-native',
    } as Record<string, string>,
  },
} as const;

export function getSkipGoConfig() {
  return _isTestnet ? SKIP_GO_CONFIG.testnet : SKIP_GO_CONFIG.mainnet;
}

// ---------------------------------------------------------------------------
// Jupiter (Solana) Configuration
// ---------------------------------------------------------------------------
const JUPITER_CONFIG = {
  mainnet: {
    apiBase: 'https://quote-api.jup.ag/v6',
    tokens: {
      'SOL': 'So11111111111111111111111111111111111111112',
      'USDC': 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
      'USDT': 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
      'BONK': 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
      'JUP': 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',
      'RAY': '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R',
      'ORCA': 'orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE',
      'MSOL': 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So',
      'JITOSOL': 'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn',
      'PYTH': 'HZ1JovNiVvGrGNiiYvEozEVgZ58xaU3RKwX8eACQBCt3',
      'WIF': 'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm',
    } as Record<string, string>,
    network: 'mainnet' as const,
  },
  testnet: {
    apiBase: 'https://quote-api.jup.ag/v6', // Jupiter supports devnet on same API
    tokens: {
      'SOL': 'So11111111111111111111111111111111111111112',
      'USDC': '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU', // Devnet USDC
      'USDT': 'EJwZgeZrdC8TXTQbQBoL6bfuAnFUQYtEnEAp8HUBkP6q', // Devnet USDT
    } as Record<string, string>,
    network: 'devnet' as const,
  },
} as const;

export function getJupiterConfig() {
  return _isTestnet ? JUPITER_CONFIG.testnet : JUPITER_CONFIG.mainnet;
}

// ---------------------------------------------------------------------------
// STON.fi (TON) Configuration
// ---------------------------------------------------------------------------
const STONFI_CONFIG = {
  mainnet: {
    apiBase: 'https://api.ston.fi',
    routerAddress: 'EQB3ncyBUTjZUA5EnFKR5_EnOMI9V1tTEAAPaiU71gc4TiUt',
    tokens: {
      'TON': 'EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c',
      'USDT': 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs',
      'USDC': 'EQC61IQRl0_la95t27xhIpjxZt32vLPpRn4SJT6yMBPk_HRt',
      'STON': 'EQA2kCVNwVsil2EM2mB0SkXytxCqWqSUeNj8RbnUqeC8mBqW',
      'GEMSTON': 'EQBX6K9aXVl3nXINCyPPL86C4ONVmQ8vK360u6dykFKXpHCa',
      'BOLT': 'EQD0vdSA_NedR9uvbgN9EikRX-suesDxGeFg69XQMavfLqIw',
      'SCALE': 'EQBlqsm144Dq6SjbPI4jjZvA1hqTIP3CvHovbIfW_t-SCALE',
      'JETTON': 'EQBynBO23ywHy_CgarY9NK9FTz0yDsG82PtcbSTQgGoXwiuA',
    } as Record<string, string>,
    network: 'mainnet' as const,
  },
  testnet: {
    apiBase: 'https://api.testnet.ston.fi',
    routerAddress: 'EQBsGx9ArADUrREB34W-ghgsCgBShvfUr8Jy3a03Nwv5FTGR', // STON.fi testnet router
    tokens: {
      'TON': 'EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c',
      'USDT': 'EQBqSpvo4S87mX9txqO4bMGPmZkQIgPeUGhQ58wlsUFQeg8z', // TON testnet USDT
      'USDC': 'EQBynBO23ywHy_CgarY9NK9FTz0yDsG82PtcbSTQgGoXwiuA', // TON testnet USDC
    } as Record<string, string>,
    network: 'testnet' as const,
  },
} as const;

export function getStonfiConfig() {
  return _isTestnet ? STONFI_CONFIG.testnet : STONFI_CONFIG.mainnet;
}

// ---------------------------------------------------------------------------
// SunSwap (TRON) Configuration
// ---------------------------------------------------------------------------
const SUNSWAP_CONFIG = {
  mainnet: {
    trongridApi: 'https://api.trongrid.io',
    routerAddress: 'TKzxdSv2FZKQrEqkKVgp5DcwEXBEKMg2Ax',
    tokens: {
      'TRX': 'T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb',
      'USDT': 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
      'USDC': 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8',
      'USDD': 'TPYmHEhy5n8TCEfYGqW2rPxsghSfzghPDn',
      'WTRX': 'TNUC9Qb1rRpS5CbWLmNMxXBjyFoydXjWFR',
      'SUN': 'TKkeiboTkxXKJpbmVFbv4a8ov5rAfRDMf9',
      'JST': 'TCFLL5dx5ZJdKnWuesXxi1VPwjLVmWZZy9',
      'BTT': 'TAFjULxiVgT4qWk6UZwjqwZXTSaGaqnVp4',
      'WIN': 'TLa2f6VPqDgRE67v1736s7bJ8Ray5wYjU7',
      'NFT': 'TFczxzPhnThNSqr5by8tvxsdCFRRz6cPNq',
    } as Record<string, string>,
    hexAddresses: {
      'TRX': '891cdb91d149f23b1a45d9c5ca78a88d0cb44c18',
      'WTRX': '891cdb91d149f23b1a45d9c5ca78a88d0cb44c18',
      'USDT': 'a614f803b6fd780986a42c78ec9c7f77e6ded13c',
      'USDC': '3487b63d30b5b2c87fb7ffa8bcfade38eaac1abe',
      'USDD': '94f24e992ca04b49c6f2a2753338ed4f14636cc7',
      'SUN': '6b5151320359f5fbb4e0c5e4a992d0e23a25f36c',
      'JST': '18fd0626daf3af02389aef3ed87db9c33f638ffa',
    } as Record<string, string>,
    network: 'mainnet' as const,
  },
  testnet: {
    trongridApi: 'https://api.shasta.trongrid.io', // Shasta testnet
    routerAddress: 'TKzxdSv2FZKQrEqkKVgp5DcwEXBEKMg2Ax', // SunSwap testnet router (Nile)
    tokens: {
      'TRX': 'T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb',
      'USDT': 'TG3XXyExBkFU9nQGoc7C2VYjqFL5pURrfN', // Shasta USDT
      'USDC': 'TEMVynQpntMqkPxP5SJXXX8HosrTfQGDAS', // Shasta USDC
      'WTRX': 'TNUC9Qb1rRpS5CbWLmNMxXBjyFoydXjWFR',
    } as Record<string, string>,
    hexAddresses: {
      'TRX': '891cdb91d149f23b1a45d9c5ca78a88d0cb44c18',
      'WTRX': '891cdb91d149f23b1a45d9c5ca78a88d0cb44c18',
    } as Record<string, string>,
    network: 'testnet' as const,
  },
} as const;

export function getSunswapConfig() {
  return _isTestnet ? SUNSWAP_CONFIG.testnet : SUNSWAP_CONFIG.mainnet;
}

// ---------------------------------------------------------------------------
// BTTC Bridge Configuration
// ---------------------------------------------------------------------------
interface BttcBridgeContracts {
  tron: { rootChainManager: string; erc20Predicate: string };
  bttc: { childChainManager: string; stateReceiver: string };
  ethereum: { rootChainManager: string; erc20Predicate: string };
  bsc: { rootChainManager: string; erc20Predicate: string };
}

interface BttcNetworkConfig {
  contracts: BttcBridgeContracts;
  bridgeableTokens: Record<string, { symbol: string; decimals: number; tron?: string; bttc?: string; ethereum?: string; bsc?: string }>;
}

const BTTC_CONFIG: Record<'mainnet' | 'testnet', BttcNetworkConfig> = {
  mainnet: {
    contracts: {
      tron: {
        rootChainManager: 'TGBr8uh9JBBrNvpRLwnTHupLuvWr9EUdxq',
        erc20Predicate: 'TDfvjKaB2H3rDwTRDyuY4j3NWkAA5kEdE7',
      },
      bttc: {
        childChainManager: '0x9a15f3a682d086c515be4037bda3b0676203a8ef',
        stateReceiver: '0x0000000000000000000000000000000000001001',
      },
      ethereum: {
        rootChainManager: '0xAE79a87DDb8CD5A3C1CFfb7A7fd51D0D4d3FFd1f',
        erc20Predicate: '0xE739B1f92b78c84F2c1316fFc3B4A0a3C44b81e8',
      },
      bsc: {
        rootChainManager: '0xAE79a87DDb8CD5A3C1CFfb7A7fd51D0D4d3FFd1f',
        erc20Predicate: '0xE739B1f92b78c84F2c1316fFc3B4A0a3C44b81e8',
      },
    },
    bridgeableTokens: {
      BTT: { symbol: 'BTT', decimals: 18, tron: 'TAFjULxiVgT4qWk6UZwjqwZXTSaGaqnVp4', bttc: '0x0000000000000000000000000000000000001010', ethereum: '0xC669928185DbCE49d2230CC9B0979BE6DC797957', bsc: '0x352Cb5E19b12FC216548a2677bD0fce83BaE434B' },
      TRX: { symbol: 'TRX', decimals: 6, tron: 'TNUC9Qb1rRpS5CbWLmNMxXBjyFoydXjWFR', bttc: '0xEdf53026aeA60f8F75FcA25f8830b7e2d6200662' },
      USDT: { symbol: 'USDT', decimals: 6, tron: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', bttc: '0xdB28719F7f938507dBfe4f0eAe55668903D34a15', ethereum: '0xdAC17F958D2ee523a2206206994597C13D831ec7', bsc: '0x55d398326f99059fF775485246999027B3197955' },
      USDC: { symbol: 'USDC', decimals: 6, tron: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8', bttc: '0xCa424b845497f7204D9301bd13Ff87C0E2e86FCF', ethereum: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', bsc: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d' },
      USDD: { symbol: 'USDD', decimals: 18, tron: 'TPYmHEhy5n8TCEfYGqW2rPxsghSfzghPDn', bttc: '0x17F235FD5974318E4E2a5e37919a209f7c37A6d1' },
      SUN: { symbol: 'SUN', decimals: 18, tron: 'TKkeiboTkxXKJpbmVFbv4a8ov5rAfRDMf9', bttc: '0x6a08dC1627F35E93E4E1f6cE4a2c2bDc7E50F90E' },
      JST: { symbol: 'JST', decimals: 18, tron: 'TCFLL5dx5ZJdKnWuesXxi1VPwjLVmWZZy9', bttc: '0x39E4546B92e6E0D5cC9b14e0f5fA31d7C2f6e8E6' },
      WIN: { symbol: 'WIN', decimals: 6, tron: 'TLa2f6VPqDgRE67v1736s7bJ8Ray5wYjU7', bttc: '0x89E97C1b3E76D1F6f6aAEC7bAC7C0d9c95c2D0a9' },
    },
  },
  testnet: {
    contracts: {
      tron: {
        rootChainManager: 'TGBr8uh9JBBrNvpRLwnTHupLuvWr9EUdxq', // BTTC Donau testnet
        erc20Predicate: 'TDfvjKaB2H3rDwTRDyuY4j3NWkAA5kEdE7',
      },
      bttc: {
        childChainManager: '0x9a15f3a682d086c515be4037bda3b0676203a8ef',
        stateReceiver: '0x0000000000000000000000000000000000001001',
      },
      ethereum: {
        rootChainManager: '0xBbD7cBFA79faee899Eaf900F13C9065bF03B1A74', // Sepolia BTTC testnet
        erc20Predicate: '0xdD6596F2029e6233DEFfaCa232C7C4B78eFD3098',
      },
      bsc: {
        rootChainManager: '0xBbD7cBFA79faee899Eaf900F13C9065bF03B1A74',
        erc20Predicate: '0xdD6596F2029e6233DEFfaCa232C7C4B78eFD3098',
      },
    },
    bridgeableTokens: {
      BTT: { symbol: 'BTT', decimals: 18, tron: 'TAFjULxiVgT4qWk6UZwjqwZXTSaGaqnVp4', bttc: '0x0000000000000000000000000000000000001010' },
      TRX: { symbol: 'TRX', decimals: 6, tron: 'TNUC9Qb1rRpS5CbWLmNMxXBjyFoydXjWFR', bttc: '0xEdf53026aeA60f8F75FcA25f8830b7e2d6200662' },
      USDT: { symbol: 'USDT', decimals: 6, tron: 'TG3XXyExBkFU9nQGoc7C2VYjqFL5pURrfN', bttc: '0xdB28719F7f938507dBfe4f0eAe55668903D34a15', ethereum: '0x7169D38820dfd117C3FA1f22a697dBA58d90BA06' },
      USDC: { symbol: 'USDC', decimals: 6, tron: 'TEMVynQpntMqkPxP5SJXXX8HosrTfQGDAS', bttc: '0xCa424b845497f7204D9301bd13Ff87C0E2e86FCF', ethereum: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238' },
    },
  },
};

export function getBttcConfig() {
  return _isTestnet ? BTTC_CONFIG.testnet : BTTC_CONFIG.mainnet;
}

// ---------------------------------------------------------------------------
// Canton Bridge Configuration
// ---------------------------------------------------------------------------
interface CantonBridgeBaseConfig {
  base: {
    chainId: string;
    chainName: string;
    usdcAddress: string;
    bridgeContractAddress: string;
    rpcUrl: string;
  };
}

const CANTON_BRIDGE_CONFIG_MAP: Record<'mainnet' | 'testnet', CantonBridgeBaseConfig> = {
  mainnet: {
    base: {
      chainId: '8453',
      chainName: 'Base',
      usdcAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      bridgeContractAddress: '',
      rpcUrl: 'https://mainnet.base.org',
    },
  },
  testnet: {
    base: {
      chainId: '84532',          // Base Sepolia
      chainName: 'Base Sepolia',
      usdcAddress: '0x036CbD53842c5426634e7929541eC2318f3dCF7e', // Base Sepolia USDC
      bridgeContractAddress: '',
      rpcUrl: 'https://sepolia.base.org',
    },
  },
};

export function getCantonBridgeNetworkConfig() {
  return _isTestnet ? CANTON_BRIDGE_CONFIG_MAP.testnet : CANTON_BRIDGE_CONFIG_MAP.mainnet;
}

// ---------------------------------------------------------------------------
// Swap Router Chain Types (dynamic per network)
// ---------------------------------------------------------------------------
export function getChainTypes(): Record<string, { chainId: string; chainType: string }> {
  const mode = _isTestnet ? 'testnet' : 'mainnet';
  const evmIds = EVM_CHAIN_IDS[mode];

  return {
    // EVM
    'ethereum': { chainId: evmIds.ethereum, chainType: 'evm' },
    'base': { chainId: evmIds.base, chainType: 'evm' },
    'arbitrum': { chainId: evmIds.arbitrum, chainType: 'evm' },
    'optimism': { chainId: evmIds.optimism, chainType: 'evm' },
    'polygon': { chainId: evmIds.polygon, chainType: 'evm' },
    'avalanche': { chainId: evmIds.avalanche, chainType: 'evm' },
    'bnb chain': { chainId: evmIds.bsc, chainType: 'evm' },
    'bsc': { chainId: evmIds.bsc, chainType: 'evm' },
    // Cosmos
    'osmosis': { chainId: _isTestnet ? 'osmo-test-5' : 'osmosis-1', chainType: 'cosmos' },
    'cosmos hub': { chainId: _isTestnet ? 'theta-testnet-001' : 'cosmoshub-4', chainType: 'cosmos' },
    'cosmos': { chainId: _isTestnet ? 'theta-testnet-001' : 'cosmoshub-4', chainType: 'cosmos' },
    'noble': { chainId: _isTestnet ? 'grand-1' : 'noble-1', chainType: 'cosmos' },
    'celestia': { chainId: _isTestnet ? 'mocha-4' : 'celestia', chainType: 'cosmos' },
    // Solana
    'solana': { chainId: _isTestnet ? 'solana-devnet' : 'solana', chainType: 'solana' },
    // TON
    'ton': { chainId: 'ton', chainType: 'ton' },
    // TRON
    'tron': { chainId: 'tron', chainType: 'tron' },
    'trx': { chainId: 'tron', chainType: 'tron' },
    // Canton
    'canton': { chainId: 'canton', chainType: 'canton' },
    'canton network': { chainId: 'canton', chainType: 'canton' },
    'canton-network': { chainId: 'canton', chainType: 'canton' },
  };
}

// ---------------------------------------------------------------------------
// SDK network string helpers (each chain SDK expects different values)
// ---------------------------------------------------------------------------

/** Generic network: 'mainnet' | 'testnet' */
export function getSdkNetwork(): 'mainnet' | 'testnet' {
  return _isTestnet ? 'testnet' : 'mainnet';
}

/** Solana SDK expects 'mainnet' | 'devnet' */
export function getSolanaSdkNetwork(): 'mainnet' | 'devnet' {
  return _isTestnet ? 'devnet' : 'mainnet';
}

/** TRON SDK expects 'mainnet' | 'shasta' */
export function getTronSdkNetwork(): 'mainnet' | 'shasta' {
  return _isTestnet ? 'shasta' : 'mainnet';
}
